// Pure, dependency-free asset-breakdown grouping for the Creative page group-by.
// Lives in plain .js (no @/ aliases, no JSX) so it is unit-testable via `node --test`.
// calculateInsights is injected by the caller (ads.jsx) to keep this file dep-free.

// Maps a group-by key to its Meta asset breakdown and how to read the group key
// off each broken-down insight row.
export const ASSET_BREAKDOWN_BY_GROUP = {
    ad_copy: {
        breakdown: 'body_asset',
        groupKeyOf: (row) => row?.body_asset?.text || null,
    },
    ad_headline: {
        breakdown: 'title_asset',
        groupKeyOf: (row) => row?.title_asset?.text || null,
    },
    ad_landing_page: {
        breakdown: 'link_url_asset',
        groupKeyOf: (row) => row?.link_url_asset?.website_url || row?.link_url_asset?.id || null,
    },
}

// Group a broken-down report (ads[] each carrying per-asset insight rows) into group
// objects shaped like generateAdGroupsReports output. Each insight row is already
// per-asset, so each row lands in exactly one bucket -> no double counting.
export const buildAssetBreakdownGroups = (adsData, groupType, calculateInsights, opts = {}) => {
    const { attributionWindowKeys, benchmark, internalCustomMetrics, platform = 'meta' } = opts

    const mapping = ASSET_BREAKDOWN_BY_GROUP[groupType]
    if (!mapping) {
        return []
    }

    const groupsMap = {}
    const groups = []

    for (const ad of adsData) {
        const adPlatform = ad._provider || platform
        const insights = Array.isArray(ad.insights) ? ad.insights : []

        for (const insight of insights) {
            const groupKey = mapping.groupKeyOf(insight)
            if (!groupKey) {
                continue
            }

            if (!groupsMap[groupKey]) {
                groupsMap[groupKey] = {
                    id: groupKey,
                    title: groupKey,
                    groupType,
                    adType: ad.adType,
                    ads: [],
                    creatives: [],
                    _adIds: new Set(),
                }
                groups.push(groupsMap[groupKey])
            }

            const group = groupsMap[groupKey]

            // Aggregate group-level metrics (calculateInsights mutates + returns the same ref).
            calculateInsights(group, insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)

            // Per-date buckets for time-series charts (mirrors generateAdGroupsReports).
            const dateKey = `by_dates_${insight.date_start}_${insight.date_stop}`
            if (!group[dateKey]) {
                group[dateKey] = {}
            }
            calculateInsights(group[dateKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)

            // Track contributing ads (deduped) + union their creatives.
            if (!group._adIds.has(ad.id)) {
                group._adIds.add(ad.id)
                group.ads.push(ad)
                group.creatives = [...new Set([...group.creatives, ...(ad.creatives || [])])]
            }
        }
    }

    // Drop the internal Set so group objects stay clean/serializable.
    for (const group of groups) {
        delete group._adIds
    }

    return groups
}
